import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Radio,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Loader2,
  ExternalLink,
  Eye,
  EyeOff,
  AlertTriangle,
  Zap,
  Youtube,
  Twitch,
  Settings,
  TestTube,
  Crown
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



// Platform icons mapping
const PlatformIcon = ({ platform, className }) => {
  switch (platform) {
    case 'twitch':
      return <Twitch className={className} />;
    case 'youtube':
      return <Youtube className={className} />;
    case 'kick':
      return <Zap className={className} />;
    case 'facebook':
      return <Radio className={className} />;
    default:
      return <Settings className={className} />;
  }
};

// Platform colors
const platformColors = {
  twitch: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  youtube: 'bg-red-500/20 text-red-400 border-red-500/30',
  kick: 'bg-green-500/20 text-green-400 border-green-500/30',
  facebook: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  custom: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

export function SocialSyncSettings() {
  const { getAuthHeaders, userRole, artistTier } = useAuth();
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [tierLimits, setTierLimits] = useState({});
  const [limit, setLimit] = useState(0);
  const [canAddMore, setCanAddMore] = useState(false);
  
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(null);
  
  const [formData, setFormData] = useState({
    platform: 'twitch',
    display_name: '',
    rtmp_url: '',
    stream_key: '',
    is_enabled: true
  });
  
  const [showStreamKey, setShowStreamKey] = useState(false);

  // Fetch platforms and destinations
  const fetchData = async () => {
    try {
      // Get platforms config
      const platformsRes = await fetch(`${API}/socialsync/platforms`);
      if (platformsRes.ok) {
        const data = await platformsRes.json();
        setPlatforms(data.platforms);
        setTierLimits(data.tier_limits);
      }
      
      // Get user's destinations
      const destRes = await fetch(`${API}/socialsync/destinations`, {
        headers: getAuthHeaders()
      });
      
      if (destRes.ok) {
        const data = await destRes.json();
        setDestinations(data.destinations);
        setLimit(data.limit);
        setCanAddMore(data.can_add_more);
      } else if (destRes.status === 403) {
        // User doesn't have access
        setDestinations([]);
        setLimit(0);
        setCanAddMore(false);
      }
    } catch (error) {
      console.error('Error fetching social sync data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle platform change
  const handlePlatformChange = (platform) => {
    setFormData(prev => ({
      ...prev,
      platform,
      rtmp_url: platforms[platform]?.default_rtmp_url || '',
      display_name: prev.display_name || platforms[platform]?.name || ''
    }));
  };

  // Add destination
  const handleAdd = async () => {
    if (!formData.display_name || !formData.stream_key) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/socialsync/destinations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Destination added successfully');
        setShowAddForm(false);
        setFormData({
          platform: 'twitch',
          display_name: '',
          rtmp_url: '',
          stream_key: '',
          is_enabled: true
        });
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to add destination');
      }
    } catch (error) {
      toast.error('Failed to add destination');
    } finally {
      setSubmitting(false);
    }
  };

  // Update destination
  const handleUpdate = async (destinationId) => {
    setSubmitting(true);
    try {
      const updateData = {
        display_name: formData.display_name,
        rtmp_url: formData.rtmp_url,
        is_enabled: formData.is_enabled
      };
      
      // Only include stream key if it was changed
      if (formData.stream_key) {
        updateData.stream_key = formData.stream_key;
      }
      
      const response = await fetch(`${API}/socialsync/destinations/${destinationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        toast.success('Destination updated');
        setEditingId(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update destination');
      }
    } catch (error) {
      toast.error('Failed to update destination');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete destination
  const handleDelete = async (destinationId) => {
    if (!confirm('Are you sure you want to delete this destination?')) return;
    
    try {
      const response = await fetch(`${API}/socialsync/destinations/${destinationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Destination deleted');
        fetchData();
      } else {
        toast.error('Failed to delete destination');
      }
    } catch (error) {
      toast.error('Failed to delete destination');
    }
  };

  // Test destination
  const handleTest = async (destinationId) => {
    setTesting(destinationId);
    try {
      const response = await fetch(`${API}/socialsync/destinations/${destinationId}/test`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  // Toggle enabled status
  const handleToggleEnabled = async (dest) => {
    try {
      const response = await fetch(`${API}/socialsync/destinations/${dest.destination_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ is_enabled: !dest.is_enabled })
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update destination');
    }
  };

  // Start editing
  const startEdit = (dest) => {
    setEditingId(dest.destination_id);
    setFormData({
      platform: dest.platform,
      display_name: dest.display_name,
      rtmp_url: dest.rtmp_url,
      stream_key: '', // Don't populate - user must enter new if changing
      is_enabled: dest.is_enabled
    });
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  // Check if user has access
  const allowedRoles = ['streamer', 'podcaster', 'owner', 'admin'];
  if (!allowedRoles.includes(userRole)) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <Radio className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="font-heading text-lg font-bold mb-2">Social Sync</h3>
          <p className="text-muted-foreground">
            This feature is only available for Streamers and Podcasters.
          </p>
        </div>
      </Card>
    );
  }

  // Check tier access
  const userLimit = tierLimits[artistTier] ?? 0;
  if (userLimit === 0 && !['owner', 'admin'].includes(userRole)) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <Crown className="w-12 h-12 mx-auto mb-4 text-amber-500 opacity-50" />
          <h3 className="font-heading text-lg font-bold mb-2">Upgrade Required</h3>
          <p className="text-muted-foreground mb-4">
            Social Sync requires a Pro tier or higher subscription.
          </p>
          <Button onClick={() => window.location.href = '/pricing'}>
            View Plans
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Radio className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold">Social Sync</h3>
            <p className="text-sm text-muted-foreground">
              Stream to multiple platforms simultaneously
            </p>
          </div>
        </div>
        
        <Badge variant="outline">
          {destinations.length} / {limit === -1 ? '∞' : limit} destinations
        </Badge>
      </div>

      {/* Disclaimer */}
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <p className="font-medium text-amber-500">Platform Compliance Notice</p>
            <p className="text-sm text-muted-foreground">
              Some platforms have rules about simulcasting. You are responsible for compliance 
              with each platform&apos;s terms of service. DMV Soundstage is not responsible for 
              any strikes, bans, or enforcement actions from third-party platforms.
            </p>
          </div>
        </div>
      </div>

      {/* Destinations List */}
      <div className="space-y-3 mb-6">
        {destinations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No streaming destinations configured yet.</p>
          </div>
        ) : (
          destinations.map(dest => (
            <div 
              key={dest.destination_id}
              className={cn(
                "p-4 rounded-lg border transition-all",
                platformColors[dest.platform] || platformColors.custom
              )}
            >
              {editingId === dest.destination_id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Display Name</Label>
                      <Input
                        value={formData.display_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>RTMP URL</Label>
                      <Input
                        value={formData.rtmp_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, rtmp_url: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>New Stream Key (leave blank to keep existing)</Label>
                    <div className="relative">
                      <Input
                        type={showStreamKey ? "text" : "password"}
                        value={formData.stream_key}
                        onChange={(e) => setFormData(prev => ({ ...prev, stream_key: e.target.value }))}
                        placeholder="Enter new stream key to change"
                      />
                      <button
                        type="button"
                        onClick={() => setShowStreamKey(!showStreamKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      >
                        {showStreamKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleUpdate(dest.destination_id)}
                      disabled={submitting}
                      size="sm"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Save
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setEditingId(null)}
                      size="sm"
                    >
                      <X className="w-4 h-4" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PlatformIcon platform={dest.platform} className="w-6 h-6" />
                    <div>
                      <p className="font-medium">{dest.display_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {platforms[dest.platform]?.name} • Key: {dest.stream_key_masked}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={dest.is_enabled}
                      onCheckedChange={() => handleToggleEnabled(dest)}
                    />
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleTest(dest.destination_id)}
                      disabled={testing === dest.destination_id}
                    >
                      {testing === dest.destination_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                    </Button>
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => startEdit(dest)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(dest.destination_id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add New Form */}
      {showAddForm ? (
        <div className="p-4 border border-border rounded-lg space-y-4">
          <h4 className="font-medium">Add Streaming Destination</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Platform</Label>
              <Select 
                value={formData.platform} 
                onValueChange={handlePlatformChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(platforms).map(([key, platform]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <PlatformIcon platform={key} className="w-4 h-4" />
                        {platform.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Display Name</Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="My Twitch Channel"
              />
            </div>
          </div>
          
          <div>
            <Label>RTMP URL</Label>
            <Input
              value={formData.rtmp_url}
              onChange={(e) => setFormData(prev => ({ ...prev, rtmp_url: e.target.value }))}
              placeholder="rtmp://..."
            />
            {platforms[formData.platform]?.help_url && (
              <a 
                href={platforms[formData.platform].help_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                Where to find this?
              </a>
            )}
          </div>
          
          <div>
            <Label>Stream Key</Label>
            <div className="relative">
              <Input
                type={showStreamKey ? "text" : "password"}
                value={formData.stream_key}
                onChange={(e) => setFormData(prev => ({ ...prev, stream_key: e.target.value }))}
                placeholder="Your stream key (stored securely)"
              />
              <button
                type="button"
                onClick={() => setShowStreamKey(!showStreamKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showStreamKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Stream keys are encrypted and never shown again after saving.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={submitting}>
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Add Destination</>
              )}
            </Button>
            <Button variant="outline" onClick={() => {
              setShowAddForm(false);
              setFormData({
                platform: 'twitch',
                display_name: '',
                rtmp_url: '',
                stream_key: '',
                is_enabled: true
              });
            }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : canAddMore ? (
        <Button 
          onClick={() => {
            setShowAddForm(true);
            handlePlatformChange('twitch');
          }}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Streaming Destination
        </Button>
      ) : (
        <div className="text-center py-4 text-muted-foreground">
          <p>You&apos;ve reached your destination limit ({limit}).</p>
          <Button variant="link" onClick={() => window.location.href = '/pricing'}>
            Upgrade for more destinations
          </Button>
        </div>
      )}
    </Card>
  );
}

export default SocialSyncSettings;
